import { writable } from "svelte/store";
import type { Writable } from "svelte/store";
import type { User, UserClaims } from "../api/types.js";
import { navigateTo } from "../routing/route-action.js";
import { ROUTE } from "../../constants/route-constants.js";
import { isAuthenticated } from "../security/auth-handler.js";
import { logInfo } from "../logging/log-handler.js";

export interface UserStore {
  user: User | null; // Database user profile
  claims: UserClaims | null; // Secured claims from token
  session_id: string | null; // Session ID for tracking user session
}

function createUserStore() {
  const { subscribe, set, update }: Writable<UserStore> = writable({
    user: null,
    claims: null,
    session_id: null,
  });

  return {
    subscribe,
    setUser: (user: User, session_id?: string) => {
      logInfo(
        "Setting user in store",
        "user-store",
        {},
        {
          userId: user.email,
          sessionId: session_id,
          hasClaims: !!user.claims,
        },
      );
      update((store) => ({
        ...store,
        user,
        claims: user.claims,
        session_id: session_id || store.session_id,
      }));
      localStorage.setItem("user", JSON.stringify(user));
      if (session_id) {
        localStorage.setItem("sessionId", session_id);
      }
    },
    clearUser: () => {
      logInfo("Clearing user store", "user-store", {}, {});
      set({
        user: null,
        claims: null,
        session_id: null,
      });
      localStorage.removeItem("user");
      localStorage.removeItem("sessionId");
    },
    updateMetadata: (metadata: any) => {
      update((store) => ({
        ...store,
        user: store.user ? { ...store.user, metadata } : null,
      }));
    },
    signOut: () => {
      localStorage.removeItem("user");
      localStorage.removeItem("sessionId");
      isAuthenticated.set(false);
      localStorage.setItem("auth", "false");
      userStore.clearUser();
      navigateTo(ROUTE.LOGIN);
    },
    setSessionId: (session_id: string) => {
      update((store) => ({
        ...store,
        session_id,
      }));
      localStorage.setItem("sessionId", session_id);
    },
  };
}

export const userStore = createUserStore();
